<template>
  <div class="flex flex-col gap-1">
    <el-form size="mini">
      <el-card class="box-card dense">
        <div slot="header">City/State Rates</div>
        <Regions
          :hub="hub"
          :terminals="options.terminals"
          :depots="options.depots"
          :yards="options.yards"
          :places="options.places"
        />
        <fieldset class="form" v-if="!directoryPlan">
          <legend>Road vs Local Boundary</legend>
          <div class="flex items-center gap-1">
            <div class="w-24">
              <el-input
                v-model="baseRate.road_vs_local"
                size="mini"
                type="number"
              />
            </div>
            <div class="text-xs text-gray-600">
              For example, if Boundary is $350, then less than $350 is Local,
              otherwise Road.
            </div>
          </div>
        </fieldset>
        <fieldset class="form">
          <legend>Base Rate</legend>
          <div class="flex flex-col gap-1">
            <slot v-if="!directoryPlan">
              <div class="flex items-center">
                <div class="select-prepend">
                  Extra Actions (Base Rate)
                  <el-tooltip
                    class="item"
                    effect="dark"
                    content="Select extra actions to apply base rate"
                  >
                    <i class="fas fa-info-circle" />
                  </el-tooltip>
                </div>
                <div class="w-96">
                  <el-select
                    v-model="baseRate.extra_actions"
                    size="mini"
                    multiple
                    filterable
                    clearable
                    class="w-full"
                  >
                    <el-option-group
                      v-for="(group, type) in options.categories"
                      :key="type"
                      :label="type"
                    >
                      <slot v-for="item in group">
                        <el-option
                          :key="item.id"
                          :label="item.name"
                          :value="item.id"
                          :class="type"
                        />
                      </slot>
                    </el-option-group>
                  </el-select>
                </div>
              </div>
              <fieldset class="form flex flex-col gap-1" v-if="baseRate.driver">
                <legend>Driver Rates</legend>
                <div class="flex gap-1">
                  <div class="flex items-center">
                    <div class="select-prepend"> Mode </div>
                    <div class="w-24">
                      <el-select v-model="baseRate.driver.mode" size="mini">
                        <el-option label="Fixed" value="fixed" />
                        <el-option label="Percent" value="percent" />
                        <el-option label="Mileage" value="mileage" />
                        <el-option label="Zone" value="zone" />
                      </el-select>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <div class="select-prepend"> Group </div>
                    <div class="w-20">
                      <el-select v-model="baseRate.driver.group" size="mini">
                        <el-option label="Global" value="global"></el-option>
                        <el-option label="Role" value="role"></el-option>
                      </el-select>
                    </div>
                  </div>
                  <slot v-if="driverPercentRate">
                    <div
                      v-if="driverRateGroupByRole"
                      class="flex items-center gap-1"
                    >
                      <div
                        v-for="(role, abbrev) in options.driverTypes"
                        :key="abbrev"
                        class="-mt-3.5"
                      >
                        <label class="text-2xs">{{ role }}</label>
                        <div class="flex items-center">
                          <div class="w-28">
                            <el-input
                              v-model="baseRate.driver[`${abbrev}road`]"
                              size="mini"
                              type="number"
                            >
                              <template slot="prepend">
                                <span class="text-gray-600">Road</span>
                              </template>
                              <template slot="append">%</template>
                            </el-input>
                          </div>
                          <div class="w-28">
                            <el-input
                              v-model="baseRate.driver[`${abbrev}local`]"
                              size="mini"
                              type="number"
                            >
                              <template slot="prepend">
                                <span class="text-gray-600">Local</span>
                              </template>
                              <template slot="append">%</template>
                            </el-input>
                          </div>
                        </div>
                      </div>
                    </div>
                    <slot v-else>
                      <div class="w-24">
                        <el-input
                          v-model="baseRate.driver.road"
                          size="mini"
                          type="number"
                        >
                          <template slot="prepend">
                            <span class="text-gray-600">Road</span>
                          </template>
                          <template slot="append">%</template>
                        </el-input>
                      </div>
                      <div class="w-24 -ml-1">
                        <el-input
                          v-model="baseRate.driver.local"
                          size="mini"
                          type="number"
                        >
                          <template slot="prepend">
                            <span class="text-gray-600">Local</span>
                          </template>
                          <template slot="append">%</template>
                        </el-input>
                      </div>
                    </slot>
                  </slot>
                </div>
                <DriverMiles
                  v-if="driverMileageRate"
                  :driver="baseRate.driver"
                  :driverTypes="options.driverTypes"
                />
                <DriverZones
                  v-if="driverZoneRate"
                  :driver="baseRate.driver"
                  :driverTypes="options.driverTypes"
                  :options="options"
                />
              </fieldset>
            </slot>
            <fieldset class="form relative">
              <legend>
                City/State Rates
                <span v-if="driverMileageRate">for Customers/Warehouses</span>
                <div class="absolute right-0 -top-1" v-if="!directoryPlan">
                  <el-button
                    size="mini"
                    class="narrow"
                    @click="exportCsv('customer_rates')"
                  >
                    Export Customer Rates
                  </el-button>
                </div>
              </legend>
              <fieldset v-if="dupCityStates.length" class="border">
                <legend class="red"
                  >Duplicate City/State (Click to Focus)</legend
                >
                <div
                  class="flex flex-wrap items-center gap-x-2 gap-y-1 bg-pink-50 p-1"
                >
                  <div
                    v-for="name in dupCityStates"
                    :key="name"
                    class="tag cursor-pointer"
                    @click="focusDupCity(name)"
                  >
                    {{ name }}
                  </div>
                </div>
              </fieldset>
              <table class="tbl">
                <thead>
                  <tr>
                    <th class="w-6">
                      <a @click="addCity" class="fas fa-plus-circle" />
                    </th>
                    <th class="w-56">
                      <el-input
                        v-model="cities.keyword"
                        size="mini"
                        clearable
                        placeholder="min 2 letters"
                      >
                        <template slot="append">
                          <i class="el-icon-search" />
                        </template>
                      </el-input>
                    </th>
                    <th v-for="region in regions" :key="region.id" class="w-60">
                      {{ region.name }}
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody :key="`cities${cities.identifier}`">
                  <slot v-for="(city, index) in cities.items">
                    <tr :key="index">
                      <td class="text-center">
                        <el-popconfirm
                          title="Are you sure to delete this city/state?"
                          confirmButtonText="Delete"
                          cancelButtonText="Cancel"
                          @confirm="removeCity(index)"
                        >
                          <a
                            class="fas fa-trash-alt"
                            slot="reference"
                            v-on:click.stop
                          />
                        </el-popconfirm>
                      </td>
                      <td>
                        <div class="flex items-center gap-1">
                          <div class="w-44">
                            <el-input
                              v-if="city.manual"
                              v-model="city.name"
                              size="mini"
                              :key="`city${index}`"
                              placeholer="City/State"
                              @blur="checkCity(city)"
                            />
                            <map-suggest
                              v-else
                              v-model="city.name"
                              :cities="true"
                              :key="`map${index}`"
                              :appendBody="true"
                            />
                          </div>
                          <i
                            class="fas fa-map-location-dot text-xs cursor-pointer text-blue-500"
                            :class="{ 'text-gray-300': city.manual }"
                            @click="manualCity(city)"
                          />
                        </div>
                      </td>
                      <td v-for="region in regions" :key="region.id">
                        <div class="flex items-center justify-center gap-0.5">
                          <div class="w-16">
                            <el-input
                              v-model="city.regions[region.name].crate"
                              size="mini"
                              type="number"
                              :disabled="
                                isDupCity(city) || isEmptyData(city.name)
                              "
                            >
                              <template slot="prepend">C</template>
                            </el-input>
                          </div>
                          <slot v-if="driverFixedRate">
                            <div
                              v-if="driverRateGroupByRole"
                              class="flex items-center gap-1"
                            >
                              <div
                                v-for="(role, abbrev) in options.driverTypes"
                                :key="abbrev"
                                class="w-16"
                              >
                                <el-input
                                  v-model="
                                    city.regions[region.name][`${abbrev}drate`]
                                  "
                                  size="mini"
                                  type="number"
                                  :disabled="
                                    isDupCity(city) || isEmptyData(city.name)
                                  "
                                >
                                  <template slot="prepend">
                                    {{ abbrev.toUpperCase() }}
                                  </template>
                                </el-input>
                              </div>
                            </div>
                            <div v-else class="w-16">
                              <el-input
                                v-model="city.regions[region.name].drate"
                                size="mini"
                                type="number"
                                :disabled="
                                  isDupCity(city) || isEmptyData(city.name)
                                "
                              >
                                <template slot="prepend">D</template>
                              </el-input>
                            </div>
                          </slot>
                          <div class="w-16" v-if="!directoryPlan">
                            <el-input
                              v-model="city.regions[region.name].toll"
                              size="mini"
                              type="number"
                              :disabled="
                                isDupCity(city) || isEmptyData(city.name)
                              "
                            >
                              <template slot="prepend">
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 97 97"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M51.7902 52.9516V20.9516H51.3902L31.7902 8.85156L12.1902 21.0516H11.7902V81.1516H7.99023V89.0516H11.7902H51.7902H55.8902V81.1516H51.7902V62.8516L56.5902 59.8516L56.1902 50.2516L51.7902 52.9516ZM43.9902 57.3516H19.7902V28.9516H43.9902V57.3516Z"
                                    fill="black"
                                  />
                                  <path
                                    d="M88.1895 39.952L83.5895 32.752L80.6895 34.652L81.0895 44.452L88.1895 39.952Z"
                                    fill="black"
                                  />
                                  <path
                                    d="M76.9902 36.9516L70.4902 41.1516L69.9902 41.4516L70.3902 51.1516L70.7902 50.9516L77.2902 46.7516L77.6902 46.5516L77.2902 36.8516L76.9902 36.9516Z"
                                    fill="black"
                                  />
                                  <path
                                    d="M60.2898 47.5518L59.5898 48.0518L59.9898 57.6518L60.5898 57.3518L66.9898 53.2518L66.5898 43.5518L60.2898 47.5518Z"
                                    fill="black"
                                  />
                                </svg>
                              </template>
                            </el-input>
                          </div>

                          <el-popover
                            placement="right"
                            width="400"
                            trigger="click"
                            :disabled="isDupCity(city)"
                            @after-enter="
                              specificCustomers(
                                city.regions[region.name],
                                'customers',
                              )
                            "
                          >
                            <div class="customers"></div>
                            <a
                              slot="reference"
                              :class="{
                                red: !isEmptyData(
                                  city.regions[region.name].customers,
                                ),
                              }"
                            >
                              <svg
                                class="w-4"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.5"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"
                                ></path>
                              </svg>
                            </a>
                          </el-popover>
                        </div>
                      </td>
                      <td></td>
                    </tr>
                  </slot>
                </tbody>
              </table>
              <div class="my-1" v-if="cities.total > 0">
                <el-pagination
                  background
                  layout="prev, pager, next"
                  :page-size="cities.pageSize"
                  :current-page="cities.page"
                  :total="cities.total"
                  @current-change="citiesPageChange($event)"
                />
              </div>
            </fieldset>
          </div>
        </fieldset>
      </el-card>
    </el-form>

    <el-form size="mini">
      <el-card class="box-card dense">
        <div slot="header" class="flex items-center justify-between">
          <div>Weekly FSC</div>
          <el-checkbox
            v-if="!directoryPlan"
            v-model="baseRate.fsc_static"
            border
            size="mini"
          >
            Static Fuel
          </el-checkbox>
        </div>
        <slot v-if="baseRate.fsc_static">
          <p>Please setup the fuel surcharge in the accessorial fees</p>
        </slot>
        <slot v-else>
          <table class="tbl">
            <thead>
              <tr>
                <th class="w-8" />
                <th class="w-24">
                  <a @click="addWeek" class="no-underline">+ Week</a>
                </th>
                <slot v-if="!directoryPlan">
                  <th class="w-32 text-xs tracking-tighter">Customer Road</th>
                  <th class="w-32 text-xs tracking-tighter">Customer Local</th>
                </slot>
                <slot v-else>
                  <th class="w-32 text-xs tracking-tighter">Customer</th>
                </slot>
                <slot v-if="!directoryPlan">
                  <slot v-if="!driverFscByRole">
                    <th class="w-32 text-xs tracking-tighter">
                      Driver Road
                      <el-tooltip
                        v-if="driverMileageRate"
                        placement="top"
                        content="Choose C/D to calculate FSC base on Customer or Driver"
                        :open-delay="1000"
                      >
                        <a class="fas fa-info-circle" />
                      </el-tooltip>
                    </th>
                    <th class="w-32 text-xs tracking-tighter">
                      Driver Local
                      <el-tooltip
                        v-if="driverMileageRate"
                        placement="top"
                        content="Choose C/D to calculate FSC base on Customer or Driver"
                        :open-delay="1000"
                      >
                        <a class="fas fa-info-circle" />
                      </el-tooltip>
                    </th>
                  </slot>
                  <slot v-if="driverFscByRole">
                    <slot v-for="(role, abbrev) in options.driverTypes">
                      <th
                        class="w-24 text-xs tracking-tighter truncate"
                        :key="`${abbrev}-road`"
                      >
                        {{ role }} Road
                      </th>
                      <th
                        class="w-24 text-xs tracking-tighter truncate"
                        :key="`${abbrev}-local`"
                      >
                        {{ role }} Local
                      </th>
                    </slot>
                  </slot>
                </slot>
                <th></th>
              </tr>
            </thead>
            <tbody :key="`weekly-fscs-${weeklyFscs.identifier}`">
              <slot v-for="(item, index) in weeklyFscs.items">
                <tr :key="index">
                  <td>
                    <el-popconfirm
                      title="Are you sure to delete this week?"
                      confirmButtonText="Delete"
                      cancelButtonText="Cancel"
                      @confirm="removeWeek(index)"
                    >
                      <a
                        class="fas fa-trash-alt"
                        slot="reference"
                        v-on:click.stop
                      />
                    </el-popconfirm>
                  </td>
                  <td>
                    <el-date-picker
                      v-model="item.week"
                      size="mini"
                      type="week"
                      format="yyyy-WWW"
                      value-format="yyyy-MM-dd"
                      :picker-options="pickerOptions"
                      @focus="onWeekFocus(item)"
                      @change="onWeekChange(item)"
                    />
                  </td>
                  <td>
                    <div class="flex items-center justify-center gap-0.5">
                      <div class="w-24">
                        <el-input
                          v-model="item.customer.road.rate"
                          size="mini"
                          type="number"
                        >
                          <template slot="append">%</template>
                        </el-input>
                      </div>
                      <div class="w-6">
                        <el-popover
                          placement="right"
                          width="400"
                          @after-enter="
                            specificCustomers(item.customer.road, 'customers')
                          "
                        >
                          <div class="customers"></div>
                          <a
                            slot="reference"
                            :class="{
                              red: !isEmptyData(item.customer.road.customers),
                            }"
                          >
                            <svg
                              class="w-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="1.5"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden="true"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"
                              ></path>
                            </svg>
                          </a>
                        </el-popover>
                      </div>
                    </div>
                  </td>
                  <td v-if="!directoryPlan">
                    <div class="flex items-center justify-center gap-0.5">
                      <div class="w-24">
                        <el-input
                          v-model="item.customer.local.rate"
                          size="mini"
                          type="number"
                        >
                          <template slot="append">%</template>
                        </el-input>
                      </div>
                      <div class="w-6" v-if="!directoryPlan">
                        <el-popover
                          placement="right"
                          width="400"
                          @after-enter="
                            specificCustomers(item.customer.local, 'customers')
                          "
                        >
                          <div class="customers"></div>
                          <a
                            slot="reference"
                            :class="{
                              red: !isEmptyData(item.customer.local.customers),
                            }"
                          >
                            <svg
                              class="w-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="1.5"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden="true"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"
                              ></path>
                            </svg>
                          </a>
                        </el-popover>
                      </div>
                    </div>
                  </td>
                  <slot v-if="!directoryPlan">
                    <slot v-if="!driverFscByRole">
                      <td>
                        <div class="w-28">
                          <el-input
                            v-model="item.driver.road.rate"
                            size="mini"
                            type="number"
                          >
                            <template slot="prepend">
                              <a
                                v-if="driverMileageRate"
                                @click="
                                  switchTarget(item.driver.road, 'target')
                                "
                                class="no-underline"
                              >
                                {{ targetName(item.driver.road.target) }}
                              </a>
                            </template>
                            <template slot="append">%</template>
                          </el-input>
                        </div>
                      </td>
                      <td>
                        <div class="w-28">
                          <el-input
                            v-model="item.driver.local.rate"
                            size="mini"
                            type="number"
                          >
                            <template slot="prepend">
                              <a
                                v-if="driverMileageRate"
                                @click="
                                  switchTarget(item.driver.local, 'target')
                                "
                                class="no-underline"
                              >
                                {{
                                  item.driver.local.target == "customer"
                                    ? "C"
                                    : "D"
                                }}</a
                              >
                            </template>
                            <template slot="append">%</template>
                          </el-input>
                        </div>
                      </td>
                    </slot>
                    <slot v-if="driverFscByRole">
                      <slot v-for="(role, abbrev) in options.driverTypes">
                        <td :key="`${abbrev}-road`">
                          <el-input
                            v-model="item.drivers[abbrev].road.rate"
                            size="mini"
                            type="number"
                          >
                            <template slot="append">%</template>
                          </el-input>
                        </td>
                        <td :key="`${abbrev}-local`">
                          <el-input
                            v-model="item.drivers[abbrev].local.rate"
                            size="mini"
                            type="number"
                          >
                            <template slot="append">%</template>
                          </el-input>
                        </td>
                      </slot>
                    </slot>
                  </slot>
                </tr>
              </slot>
            </tbody>
          </table>
          <div class="my-1" v-if="weeklyFscs.total > 0">
            <el-pagination
              background
              layout="prev, pager, next"
              :page-size="weeklyFscs.pageSize"
              :current-page="weeklyFscs.page"
              :total="weeklyFscs.total"
              @current-change="weeklyFscsPageChange($event)"
            />
          </div>
        </slot>
      </el-card>
    </el-form>
    <Accessorial
      v-if="accessoriesReady"
      :accessories="baseRate.accessories"
      :regions="regions"
      :options="options"
      :driverMileageRate="driverMileageRate"
      @add="addAccessory"
      @remove="removeAccessory"
    />
    <div ref="customers">
      <div class="e-form" v-if="customer.visible">
        <div class="flex items-center gap-1 my-1">
          <el-select
            size="mini"
            v-model="customer.id"
            filterable
            :placeholder="searchCustomerPlaceholder"
            class="w-full"
          >
            <el-option-group
              v-for="group in customerOptions(customer.items)"
              :key="group.label"
              :label="group.label"
            >
              <el-option
                v-for="item in group.items"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-option-group>
          </el-select>
          <el-button
            plain
            size="mini"
            type="primary"
            @click="addCustomer(customer.items)"
            :disabled="!customer.id"
            >Add</el-button
          >
        </div>
        <ul class="stripe max-h-80 overflow-auto my-1">
          <slot v-for="c in customer.items">
            <li
              v-if="customerName(c.id)"
              class="border px-1 py-0.5 stripe-item flex flex-col"
              :class="{ 'border-red-500': customer?.current?.id == c.id }"
              :key="c.id"
            >
              <div class="flex items-center gap-1">
                <el-popconfirm
                  title="Are you sure to delete this customized item?"
                  confirmButtonText="Delete"
                  cancelButtonText="Cancel"
                  @confirm="removeCustomer(customer.items, c.id)"
                >
                  <a
                    class="fas fa-trash-alt"
                    slot="reference"
                    v-on:click.stop
                  />
                </el-popconfirm>
                <label class="grow text-xs">
                  {{ customerName(c.id) }}
                </label>
                <img
                  :src="$getSvgUrl('warehouse')"
                  class="w-5"
                  :class="{
                    'opacity-100': customer?.current?.id == c.id,
                    'opacity-40': customer?.current?.id != c.id,
                  }"
                  @click="specificCustomerWarehouses(c)"
                />
                <div class="w-20">
                  <el-input v-model="c.rate" size="mini" type="number" />
                </div>
              </div>
              <ul
                class="max-h-20 bg-pink-50 border-t -mx-1 px-1 flex flex-col divide-y gap-0.5 empty:hidden"
              >
                <li v-for="w in c.warehouses" :key="w.id" class="py-0.5">
                  <div class="flex items-center">
                    <svg
                      class="-mt-2"
                      width="20"
                      height="20"
                      viewBox="0 0 0.96 0.96"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <title>l</title>
                      <path
                        d="M0.757 0.779H0.364V0.144a0.009 0.009 0 0 0 -0.009 -0.009h-0.028a0.009 0.009 0 0 0 -0.009 0.009v0.672a0.009 0.009 0 0 0 0.009 0.009h0.43a0.009 0.009 0 0 0 0.009 -0.009v-0.028a0.009 0.009 0 0 0 -0.009 -0.009z"
                      />
                    </svg>
                    <el-popconfirm
                      title="Are you sure to delete this customized item?"
                      confirmButtonText="Delete"
                      cancelButtonText="Cancel"
                      @confirm="removeCustomer(c.warehouses, w.id)"
                    >
                      <a
                        class="fas fa-trash-alt"
                        slot="reference"
                        v-on:click.stop
                      />
                    </el-popconfirm>
                    <label class="grow text-xs">{{ customerName(w.id) }}</label>
                    <div class="w-20">
                      <el-input v-model="w.rate" size="mini" type="number" />
                    </div>
                  </div>
                </li>
              </ul>
            </li>
          </slot>
        </ul>
      </div>
    </div>
  </div>
</template>
<script>
import Regions from "@/default/pages/base_rates/regions.vue";
import Accessorial from "@/default/pages/base_rates/accessorial.vue";
import DriverMiles from "@/default/pages/base_rates/drivers/miles.vue";
import DriverZones from "@/default/pages/base_rates/drivers/zones.vue";
export default {
  name: "CityFixedWeeklyFsc",
  components: { Regions, Accessorial, DriverMiles, DriverZones },
  props: {
    hub: {
      type: Object,
      required: true,
    },
    baseRate: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      customer: {
        id: null,
        visible: false,
        items: [],
      },
      yard: {
        id: null,
        visible: false,
        items: [],
      },
      accessory: {
        category: null,
      },
      cities: {
        keyword: "",
        page: 1,
        total: 0,
        pageSize: 10,
        items: [],
        identifier: 1,
      },
      weeklyFscs: {
        current: null,
        page: 1,
        total: 0,
        pageSize: 15,
        items: [],
        identifier: 1,
      },
      fuseOptions: {
        shouldSort: true,
        threshold: 0.25,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: ["name"],
      },
      cachedWeek: null,
      accessoriesReady: false,
    };
  },
  async mounted() {
    this.refineData();
    this.citiesPageChange(this.cities.page);
    this.weeklyFscsPageChange(-1);
  },
  watch: {
    driverRateGroup(nv, ov) {
      if (nv != ov) {
        this.refineData();
      }
    },
    cityName(nv, ov) {
      this.citiesPageChange(this.cities.page);
    },
  },
  computed: {
    pickerOptions() {
      const firstDayOfWeek =
        [
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
          "sunday",
        ].indexOf(this.$store.envs.first_day_of_week) + 1;
      return { firstDayOfWeek };
    },
    regions() {
      return this.hub.regions;
    },
    driverRateGroup() {
      return this.baseRate?.driver?.group;
    },
    driverRateGroupByRole() {
      return this.driverRateGroup == "role";
    },
    driverFixedRate() {
      return this.baseRate?.driver?.mode == "fixed";
    },
    driverPercentRate() {
      return this.baseRate?.driver?.mode == "percent";
    },
    driverMileageRate() {
      return this.baseRate?.driver?.mode == "mileage";
    },
    driverZoneRate() {
      return this.baseRate?.driver?.mode == "zone";
    },
    driverFscByRole() {
      return (
        (this.driverMileageRate || this.driverZoneRate) &&
        this.driverRateGroupByRole
      );
    },
    customerName() {
      return function (id) {
        return [...this.options.customers, ...this.options.warehouses].find(
          (o) => o.id == id,
        )?.name;
      };
    },
    customerOptions() {
      return function (group) {
        const customers = this.options.customers.filter(
          (o) => !(group || []).map((o) => o.id).includes(o.id),
        );
        const warehouses = this.options.warehouses.filter(
          (o) => !(group || []).map((o) => o.id).includes(o.id),
        );
        if (this.customer.current) {
          return [{ label: "Warehouse", items: warehouses }];
        } else {
          return [
            { label: "Customer", items: customers },
            { label: "Warehouse", items: warehouses },
          ];
        }
      };
    },
    yardName() {
      return function (id) {
        return this.options.yards.find((o) => o.id == id)?.name;
      };
    },
    yardOptions() {
      return function (group) {
        return this.options.yards.filter(
          (o) => !(group || []).map((o) => o.id).includes(o.id),
        );
      };
    },
    categoryName() {
      return function (id) {
        return Object.values(this.options.categories)
          .flat()
          .find((o) => o.id == id)?.name;
      };
    },
    fscSupported() {
      return function (id) {
        return Object.values(this.options.categories)
          .flat()
          .find((o) => o.id == id)?.fsc_required;
      };
    },
    targetName() {
      return function (target) {
        return target == "customer" ? "C" : "D";
      };
    },
    isTollFee() {
      return function (item) {
        return this.categoryName(item.category) == "Toll Fee";
      };
    },
    skipCustomer() {
      return function (item) {
        return this.isTollFee(item);
      };
    },
    cityName() {
      return this.cities.keyword;
    },
    dupCityStates() {
      const counts = {};
      (this.baseRate.cities || []).forEach((city) => {
        const name = (city.name || "").trim();
        if (this.isEmptyData(name)) return;
        counts[name] = (counts[name] || 0) + 1;
      });
      return Object.keys(counts)
        .filter((name) => counts[name] > 1)
        .sort((a, b) =>
          a
            .split(", ")
            .reverse()
            .join(" ")
            .localeCompare(b.split(", ").reverse().join(" ")),
        );
    },
    filteredCities() {
      let items = this.baseRate.cities || [];
      const term = this.cities.keyword.trim();
      if (term.length >= 2) {
        const exact = items.filter((o) => (o.name || "").trim() == term);
        items = exact.length
          ? exact
          : this.$search(term, items, this.fuseOptions).map((o) => o.item);
      }
      return items;
    },
    searchCustomerPlaceholder() {
      return this.customer.current
        ? `Search Warehouse for ${this.customerName(this.customer.current.id)}`
        : "Search Customer/Warehouse";
    },
  },
  methods: {
    refineData() {
      const br = this.baseRate;
      this.$set(br, "road_vs_local", br.road_vs_local || 0);
      this.$set(br, "cities", br.cities || []);
      this.$set(br, "fsc", br.fsc || []);
      if (!this.directoryPlan) {
        this.$set(br, "driver", br.driver || {});
        this.$set(br.driver, "mode", br.driver.mode || "percent");
        this.$set(br.driver, "road", br.driver.road || 0);
        this.$set(br.driver, "local", br.driver.local || 0);
        this.$set(br.driver, "cdroad", br.driver.cdroad || 0);
        this.$set(br.driver, "cdlocal", br.driver.cdlocal || 0);
        this.$set(br.driver, "ooroad", br.driver.ooroad || 0);
        this.$set(br.driver, "oolocal", br.driver.oolocal || 0);
        this.$set(br.driver, "bkroad", br.driver.bkroad || 0);
        this.$set(br.driver, "bklocal", br.driver.bklocal || 0);
        this.$set(br.driver, "milerates", br.driver.milerates || { rates: [] });
      }
      this.$set(br, "accessories", br.accessories || []);

      br.cities.forEach((c) => {
        this.$delete(c, "city");
        this.$delete(c, "dup");
        if (!c.regions) this.$set(c, "regions", {});
        this.regions.forEach((region) => {
          if (!Object.keys(c.regions).includes(region.name)) {
            this.$set(c.regions, region.name, {
              crate: 0,
              drate: 0,
              cddrate: 0,
              oodrate: 0,
              bkdrate: 0,
              toll: 0,
              customers: [],
            });
          }
        });
      });

      br.fsc.forEach((f) => {
        if (this.driverRateGroupByRole) {
          if (!f.drivers) this.$set(f, "drivers", {});
          Object.keys(this.options.driverTypes).forEach((role) => {
            if (!f.drivers[role])
              this.$set(f.drivers, role, {
                road: { rate: 0 },
                local: { rate: 0 },
              });
          });
        }
      });

      br.accessories.forEach((c) => {
        if (!c.regions) this.$set(c, "regions", {});
        this.regions.forEach((region) => {
          if (!Object.keys(c.regions).includes(region.name)) {
            this.$set(c.regions, region.name, {
              customer: { fee: 0, fsc: 0 },
              driver: { fee: 0, fsc: 0, target: "customer" },
            });
          }
        });
      });
      this.accessoriesReady = true;
    },
    addCity() {
      this.cities.keyword = "";
      const attrs = {
        crate: 0,
        drate: 0,
        cddrate: 0,
        oodrate: 0,
        bkdrate: 0,
        toll: 0,
      };
      const map = this.regions.map((region) => [
        region.name,
        { ...attrs, customers: [] },
      ]);
      const regions = Object.fromEntries(new Map(map));
      this.baseRate.cities.push({ name: "", regions });
      const groups = this._.chunk(this.baseRate.cities, this.cities.pageSize);
      this.citiesPageChange(groups.length);
    },
    removeCity(index) {
      const row = this.cities.items[index];
      const gindex = this.baseRate.cities.indexOf(row);
      if (gindex >= 0) this.baseRate.cities.splice(gindex, 1);
      this.citiesPageChange(this.cities.page);
    },
    focusDupCity(name) {
      this.cities.keyword = name;
      this.citiesPageChange(1);
    },
    isDupCity(city) {
      const name = (city.name || "").trim();
      if (this.isEmptyData(name) || !this.dupCityStates.includes(name))
        return false;
      return (
        this.baseRate.cities.find((o) => (o.name || "").trim() == name) != city
      );
    },
    manualCity(item) {
      this.$set(item, "manual", !item.manual);
    },
    checkCity(current) {
      if (current && current.name) {
        let [city, state] = current.name.split(",").map((o) => o.trim());
        if (city && state) {
          city = city
            .split(/\s/)
            .map((o) => o.titleize())
            .join(" ");
          state = state.toUpperCase();
          this.$set(current, "name", [city, state].join(", "));
        }
      }
    },
    citiesPageChange(val) {
      this.cities.identifier++;
      this.cities.total = this.filteredCities.length;
      const groups = this._.chunk(this.filteredCities, this.cities.pageSize);
      const page = Math.min(val, groups.length);
      this.cities.items = page == 0 ? [] : groups[page - 1];
      this.cities.page = Math.max(page, 1);
    },
    weeklyFscsPageChange(val) {
      this.weeklyFscs.identifier++;
      this.weeklyFscs.total = this.baseRate.fsc.length;
      const groups = this._.chunk(this.baseRate.fsc, this.weeklyFscs.pageSize);
      const page = val < 0 ? groups.length : Math.min(val, groups.length);
      this.weeklyFscs.items = page == 0 ? [] : groups[page - 1];
      this.weeklyFscs.page = Math.max(page, 1);
    },
    specificCustomers(target, property) {
      target[property] ||= [];
      const selector = '.el-popper[aria-hidden="false"] .customers';
      const div = document.querySelector(selector);
      if (div) {
        this.customer.visible = true;
        div.appendChild(this.$refs.customers);
        this.customer.items = target[property];
      }
    },
    addCustomer(group) {
      if (this.customer.current) {
        this.customer.current.warehouses.push({
          id: this.customer.id,
          rate: 0,
        });
      } else {
        group.push({ id: this.customer.id, rate: 0 });
      }
      this.customer.id = null;
    },
    removeCustomer(group, id) {
      let index = group.findIndex((o) => o.id == id);
      if (index >= 0) group.splice(index, 1);
    },
    specificCustomerWarehouses(item) {
      this.$set(item, "warehouses", item.warehouses || []);
      if (this.customer.current?.id == item.id) {
        this.$set(this.customer, "current", null);
      } else {
        this.$set(this.customer, "current", item);
      }
    },
    specificYards(target, property) {
      target[property] ||= [];
      const selector = '.el-popper[aria-hidden="false"] .yards';
      const div = document.querySelector(selector);
      if (div) {
        this.yard.visible = true;
        div.appendChild(this.$refs.yard);
        this.yard.items = target[property];
      }
    },
    addYard(group) {
      group.push({ id: this.yard.id, fee: 0 });
      this.yard.id = null;
    },
    removeYard(group, id) {
      let index = group.findIndex((o) => o.id == id);
      if (index >= 0) group.splice(index, 1);
    },
    onWeekFocus(item) {
      this.cachedWeek = item.week;
    },
    formatWeekLabel(weekStr) {
      const s = String(weekStr).slice(0, 10);
      const [y, m, d] = s.split("-").map(Number);
      const date = new Date(y, m - 1, d);
      const dayNum = date.getDay() || 7;
      date.setDate(date.getDate() + 4 - dayNum);
      const yearStart = new Date(date.getFullYear(), 0, 1);
      const weekNum = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
      return `${date.getFullYear()}-${weekNum}W`;
    },
    onWeekChange(item) {
      if (!item.week) return;
      const weekNorm = String(item.week).slice(0, 10);
      const isDuplicate = this.baseRate.fsc.some((f) => {
        return f !== item && f.week && String(f.week).slice(0, 10) === weekNorm;
      });
      if (isDuplicate) {
        const label = this.formatWeekLabel(weekNorm);
        this.$message({
          message: `This week is already selected. (${label})`,
          type: "error",
        });
        this.$set(item, "week", this.cachedWeek);
      }
    },
    addWeek() {
      const last = this.baseRate.fsc[this.baseRate.fsc.length - 1];
      let fsc;

      if (last) {
        fsc = JSON.parse(JSON.stringify(last));
        fsc.week = null;
      } else {
        fsc = {
          week: null,
          customer: {
            road: { rate: 0, customers: [] },
            local: { rate: 0, customers: [] },
          },
          driver: {
            road: { rate: 0, target: "customer" },
            local: { rate: 0, target: "customer" },
          },
        };

        if (this.driverFscByRole) {
          fsc.drivers = {};
          Object.keys(this.options.driverTypes).forEach((role) => {
            fsc.drivers[role] = { road: { rate: 0 }, local: { rate: 0 } };
          });
        }
      }

      this.baseRate.fsc.push(fsc);
      const groups = this._.chunk(this.baseRate.fsc, this.weeklyFscs.pageSize);
      this.weeklyFscsPageChange(groups.length);
    },
    removeWeek(index) {
      const { page } = this.weeklyFscs;
      const gindex = (page - 1) * this.weeklyFscs.pageSize + index;
      this.baseRate.fsc.splice(gindex, 1);
      this.weeklyFscsPageChange(page);
    },
    addAccessory(accessory) {
      this.baseRate.accessories.push(accessory);
    },
    removeAccessory(index) {
      this.baseRate.accessories.splice(index, 1);
    },
    switchTarget(item, target) {
      this.$set(
        item,
        target,
        item[target] == "customer" ? "driver" : "customer",
      );
    },
    switchBoolean(item, target) {
      this.$set(item, target, !item[target]);
    },
    exportCsv(target) {
      this.getDownload(`/api/v1/base_rates/export.csv`, {
        hub_id: this.hub.id,
        target,
      });
    },
  },
};
</script>
